import styled from "styled-components";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { ClipLoaderCommon, ConfirmComponent, ProcessOrderHeader } from "@components";
import { DURATION_TIME } from "@constants";
import {
    useAppSelector,
    openToast,
    AppDispatch,
    selectCheckout,
    selectRedeemer,
    fetchRedeemer,
    createNewRedeemer,
} from "@redux";
import { AppModuleLayout } from "@layouts";
import { breakpoint } from "@utilities";
import { NavLink } from "react-router-dom";
import { LoadingStage } from "src/Interfaces/shared";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { CartBreadcrumbs } from "@routes";
import {
    ICartItemData,
    IPayNowResponseData,
    ISetCustomerData,
    // IPayNowResponseData
} from "@interfaces";
// import { cartApi } from "@api";
import _ from "lodash";
import { buildCartCheckoutContext, CheckoutCartStepLabels, transformCartItemsToUpdateCartItemsData } from "./checkoutHelpers";
import { CheckoutStep1 } from "./Components/CheckoutStep1";
import { CheckoutStep2 } from "./Components/CheckoutStep2";
import { fetchCartItems, removeCartItemAndFetch, submitCartDataAndFetch } from "./checkout";
import { cartApi } from "@api";
import { CheckoutProvider } from "./CheckoutContext";

export const Cart = () => {
    //page hook
    const dispatch = useDispatch<AppDispatch>();
    //redux state
    const { cartItems, cartItemsLoadingStage, customersLoadingStage, activeDiscount } =
        useAppSelector(selectCheckout);
    const cartCustomers = useAppSelector(selectRedeemer);
    //page state
    const [activeStepIndex, setActiveStep] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);

    // unified delete queue
    const [deleteQueue, setDeleteQueue] = useState<
        { id: string | number; type: "cart" | "group" | "product" | "item" }[]
    >([]);

    useEffect(() => {
        if (cartItemsLoadingStage === LoadingStage.INITIALIZE_FAILURE) {
            ErrorToast({ Message: "Failed to load items" });
        }
    }, [cartItemsLoadingStage]);

    useEffect(() => {
        if (customersLoadingStage === LoadingStage.INITIALIZE_FAILURE) {
            ErrorToast({ Message: "Failed to load customers" });
        }
    }, [customersLoadingStage]);

    // WHAT: empty cart
    useEffect(() => {
        if (cartItems.length === 0) {
            dispatch(
                openToast({
                    type: "error",
                    message: `Cart is empty`,
                    autoHideDuration: DURATION_TIME,
                }),
            );
        }
    }, [cartItems.length]);

    const scrollToTop = () => {
        window.scrollTo(0, 0);
    };

    const handleSaveItems = async (items: ICartItemData[]) => {
        const updateItems = transformCartItemsToUpdateCartItemsData(items);
        await dispatch(submitCartDataAndFetch(updateItems)).unwrap();
    };

    const onNextStep = async (items: ICartItemData[], nextStep: string) => {
        await handleSaveItems(items);
        setActiveStep(
            _.findIndex(CheckoutCartStepLabels, (label) => label.trim().toLowerCase() === nextStep),
        );
    };

    const handleOrderCreate = async (paymentType: string) => {
        const createOrderResponse = await cartApi.createOrder({ paymentType });
        if (createOrderResponse.status !== 200) {
            console.error(createOrderResponse);
            throw new Error();
        }
        const paymentLink = (
            createOrderResponse.data.data as IPayNowResponseData
        ).payNow.redirectUrl.replaceAll("\\", "");
        window.location.href = paymentLink;
    };

    const onOrderCreateSuccess = () => {
        dispatch(fetchCartItems());
    };

    const handleHeaderBack = () => {
        if (activeStepIndex > 0) {
            setActiveStep(activeStepIndex - 1);
            return;
        }
        window.history.back();
    };

    const showDeletePopUp = (
        items: { id: string | number; type: "cart" | "group" | "product" | "item" }[],
    ) => {
        setDeleteQueue(items);
    };

    const handleDeleteYes = async () => {
        if (!deleteQueue.length) return;
        setIsDeleting(true);
        try {
            const results = await Promise.allSettled(
                deleteQueue.map(({ id, type }) =>
                    dispatch(removeCartItemAndFetch({ id, type })).unwrap(),
                ),
            );
            const errors = results.filter((r) => r.status === "rejected");
            if (errors.length) {
                ErrorToast({
                    Message:
                        errors.length > 1 ? "Failed to delete some items" : "Failed to delete item",
                });
            }
            await dispatch(fetchCartItems()).unwrap();
        } catch (e) {
            console.error(e);
            ErrorToast({ Message: "Failed to delete items" });
        } finally {
            setIsDeleting(false);
            setDeleteQueue([]);
        }
    };

    const onCreateNewCustomer = async (newCustomerData: ISetCustomerData) => {
        const newCustomer = await dispatch(createNewRedeemer(newCustomerData)).unwrap();
        await dispatch(fetchRedeemer()).unwrap();
        return newCustomer;
    };

    useEffect(() => {
        dispatch(fetchCartItems());
        dispatch(fetchRedeemer());
        scrollToTop();
    }, []);

    const renderComponent = () => {
        const activeStep = CheckoutCartStepLabels[activeStepIndex];
        switch (activeStep) {
            case "Checkout":
                return (
                    <CheckoutStep2
                        items={cartItems}
                        moveTo={setActiveStep}
                        customers={cartCustomers.redeemer}
                        onItemsSave={(items) => handleSaveItems(items)}
                        onCreateNewCustomer={onCreateNewCustomer}
                        handleOrderCreate={handleOrderCreate}
                        onOrderCreateSuccess={onOrderCreateSuccess}
                    />
                );
            case "Cart":
                return (
                    <CheckoutStep1
                        handleDeleteItems={(ids) =>
                            showDeletePopUp(ids.map((id) => ({ id, type: "item" })))
                        }
                        handleDeleteProduct={(prodId) => {
                            showDeletePopUp([{ id: prodId, type: "product" }]);
                        }}
                        handleDeleteCart={() => showDeletePopUp([{ id: "", type: "cart" }])}
                        handleSaveItems={(items) => onNextStep(items, "checkout")}
                    />
                );
        }
    };

    return (
        <AppModuleLayout onBack={handleHeaderBack}>
            <ProcessOrderHeader
                stepLabels={CheckoutCartStepLabels}
                activeStepIndex={activeStepIndex}
                breadcrumbs={CartBreadcrumbs}
                changeStep={setActiveStep}
            />
            <Main>
                {cartItemsLoadingStage === LoadingStage.IS_INITIALIZING ||
                customersLoadingStage === LoadingStage.IS_INITIALIZING ? (
                    <Content>
                        <ClipLoaderCommon color="#31B4B9" size={70} margin={150} />
                    </Content>
                ) : cartItems.length === 0 ? (
                    <Container className="wrapper">
                        <Content>
                            <img
                                width={65}
                                height={65}
                                src="/images/cart/emptyCart.svg"
                                alt="emptyCartLogo"
                            />
                            <p>There are no items in the Cart</p>
                            <NavLink to="/">
                                <Button>Continue Browsing</Button>
                            </NavLink>
                        </Content>
                    </Container>
                ) : (
                    <CheckoutProvider
                        initialItems={cartItems}
                        checkoutContext={buildCartCheckoutContext()}
                        activeDiscount={activeDiscount?.discount}
                        >
                        {renderComponent()}
                    </CheckoutProvider>
                )}
                <ConfirmComponent
                    itemIds={deleteQueue}
                    isDeleting={isDeleting}
                    onClickOverlay={() => setDeleteQueue([])}
                    onClickBtnYes={handleDeleteYes}
                    onClickBtnNo={() => setDeleteQueue([])}
                >
                    {isDeleting
                        ? "Deleting items from cart"
                        : `Are you sure you want to delete ${
                              deleteQueue.length > 1 ? "these items" : "this item"
                          }?`}
                </ConfirmComponent>
            </Main>
        </AppModuleLayout>
    );
};

const Main = styled.div`
    flex: 1;
    align-items: center;
    background-color: #f2f2f2;

    ${breakpoint.breakTablet`
        padding-bottom: 30px;
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
    `}
`;

const Container = styled.div`
    background-color: white;
    display: flex;
    flex: 1;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
`;

const Content = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 14px;
`;

const Button = styled.button`
    border: 1px solid #31b4b9;
    padding: 12px 24px;
    border-radius: 4px;
    color: #31b4b9;
    background: none;
    cursor: pointer;

    &:hover {
        background-color: #e0f7f8;
    }
`;
